import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  FolderArchive, RefreshCw, Search, ExternalLink, FileImage, FileVideo,
  FileText, File as FileIcon, HardDrive, Filter,
} from 'lucide-react';

const KIND_LABEL = {
  evidence: 'Evidence', client_document: 'Client Doc', docusign: 'DocuSign',
  case_photo: 'Case Photo', avatar: 'Avatar', applicant: 'Applicant',
};
const KIND_COLOR = {
  evidence: 'bg-red-500/15 text-red-200 border-red-500/30',
  client_document: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  docusign: 'bg-blue-500/15 text-blue-200 border-blue-500/30',
  case_photo: 'bg-purple-500/15 text-purple-200 border-purple-500/30',
  avatar: 'bg-slate-500/15 text-slate-200 border-slate-500/30',
  applicant: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
};

const fmtBytes = (n) => {
  if (!n) return '—';
  const u = ['B','KB','MB','GB']; let i = 0, v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };

const mediaOf = (ct) => {
  if (!ct) return 'other';
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  if (ct.includes('pdf')) return 'pdf';
  return 'other';
};

const MediaIcon = ({ ct }) => {
  const kind = mediaOf(ct);
  const cls = 'w-4 h-4';
  if (kind === 'image') return <FileImage className={`${cls} text-emerald-300`} />;
  if (kind === 'video') return <FileVideo className={`${cls} text-purple-300`} />;
  if (kind === 'pdf')   return <FileText  className={`${cls} text-red-300`} />;
  return <FileIcon className={`${cls} text-slate-300`} />;
};

const AdminFileHistory = () => {
  const token = localStorage.getItem('nexus_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ kind: 'all', media_type: 'all', q: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '500');
      if (filters.kind !== 'all') qs.set('kind', filters.kind);
      if (filters.media_type !== 'all') qs.set('media_type', filters.media_type);
      if (filters.q.trim()) qs.set('q', filters.q.trim());
      const r = await fetch(`${BACKEND_URL}/api/admin/file-history?${qs}`, { headers });
      if (r.status === 403) {
        toast({ title: 'Admin access required', variant: 'destructive' });
        setData({ rows: [], count: 0, summary: { by_kind: {}, by_media: {}, total_bytes: 0 } });
        return;
      }
      if (r.ok) setData(await r.json());
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [filters, headers]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const summary = data?.summary || { by_kind: {}, by_media: {}, total_bytes: 0 };

  const openFile = (row) => {
    if (!row.file_key) return;
    const url = row.file_url && row.file_url.startsWith('/')
      ? `${BACKEND_URL}${row.file_url}`
      : row.file_url || `${BACKEND_URL}/api/files/${encodeURIComponent(row.file_key)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="admin-file-history-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: '#1D4ED8', boxShadow: '0 6px 14px -4px rgba(59,130,246,0.55)' }}>
            <FolderArchive className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">Admin · Storage</div>
            <h1 className="nx-h1 mt-1">File History</h1>
            <p className="nx-body mt-1">Every file uploaded across NexusTrac — videos, photos, PDFs, avatars — in one live index.</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" data-testid="refresh-file-history">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#94A3B8]">Total files</div>
          <div className="text-3xl font-semibold text-[#F8FAFC] mt-2" data-testid="stat-total">{data?.count ?? 0}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#94A3B8] flex items-center gap-1"><HardDrive className="w-3.5 h-3.5" /> Total size</div>
          <div className="text-3xl font-semibold text-[#F8FAFC] mt-2">{fmtBytes(summary.total_bytes)}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-300">Images</div>
          <div className="text-3xl font-semibold text-emerald-200 mt-2">{summary.by_media?.image || 0}</div>
        </CardContent></Card>
        <Card className="bg-[#131A2A] border-[#334155]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-purple-300">Videos</div>
          <div className="text-3xl font-semibold text-purple-200 mt-2">{summary.by_media?.video || 0}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                   placeholder="Search title, filename, client, case, uploader"
                   className="pl-7 bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                   data-testid="filter-search" />
          </div>
          <Select value={filters.kind} onValueChange={(v) => setFilters({ ...filters, kind: v })}>
            <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="evidence">Evidence</SelectItem>
              <SelectItem value="client_document">Client documents</SelectItem>
              <SelectItem value="docusign">DocuSign envelopes</SelectItem>
              <SelectItem value="case_photo">Case photos</SelectItem>
              <SelectItem value="avatar">Avatars</SelectItem>
              <SelectItem value="applicant">Applicant docs</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.media_type} onValueChange={(v) => setFilters({ ...filters, media_type: v })}>
            <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="filter-media"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="all">Any media</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="video">Videos</SelectItem>
              <SelectItem value="pdf">PDFs</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3"><CardTitle className="text-[#F8FAFC] text-base">Files</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="file-history-table">
              <thead className="bg-[#0B0F19] text-[#94A3B8] uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">File</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Uploaded by</th>
                  <th className="text-left px-4 py-3">Case / Client</th>
                  <th className="text-right px-4 py-3">Size</th>
                  <th className="text-left px-4 py-3">Uploaded</th>
                  <th className="text-right px-4 py-3">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}-${r.id}-${r.file_key}`} className="border-t border-[#334155] hover:bg-[#182136]" data-testid={`file-row-${r.file_key || r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MediaIcon ct={r.content_type} />
                        <div className="min-w-0">
                          <div className="text-[#F8FAFC] font-medium truncate max-w-[280px]">{r.title || r.file_name || '—'}</div>
                          <div className="text-[10px] text-[#94A3B8] mt-0.5 truncate max-w-[280px]">{r.file_name || r.file_key}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${KIND_COLOR[r.kind] || 'bg-slate-500/15 text-slate-200 border-slate-500/30'} text-[10px]`}>
                        {KIND_LABEL[r.kind] || r.kind}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[#CBD5E1]">{r.uploaded_by_name || r.uploaded_by_user_id || '—'}</td>
                    <td className="px-4 py-3 text-[#CBD5E1]">
                      {r.case_number && <div>Case {r.case_number}</div>}
                      {r.client_name && <div className="text-[11px] text-[#94A3B8]">{r.client_name}</div>}
                      {!r.case_number && !r.client_name && '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-[#CBD5E1] whitespace-nowrap">{fmtBytes(r.file_size)}</td>
                    <td className="px-4 py-3 text-[#CBD5E1] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openFile(r)}
                              className="text-blue-400 hover:text-blue-300 disabled:opacity-40"
                              disabled={!r.file_key}
                              data-testid={`open-file-${r.file_key || r.id}`}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center px-4 py-10 text-[#94A3B8]">No files match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFileHistory;
